import { routerReducer } from '@components/Router/history'
import {
  initialLibraryStore,
  LibraryActions,
  libraryReducer,
  relationsReducer as relationsCoreReducer,
} from '@octopus-do/octopus-project-core'
import { confirmationReducer } from '@store/confirmation'
import { crawlImportReducer } from '@store/crawlImport'
import { loadersReducer } from '@store/loaders'
import { aiReducer } from '@store/reducers/ai'
import { apiKeysReducer } from '@store/reducers/apiKeys'
import { apiUsageReducer } from '@store/reducers/apiUsage'
import { artboardReducer } from '@store/reducers/artboard'
import { dashboardReducer } from '@store/reducers/dashboard/reducer'
import { ga4 } from '@store/reducers/ga4'
import { managerActions } from '@store/reducers/manager/actions'
import {
  initialProjectManagerStore,
  projectManagerReducer,
} from '@store/reducers/manager/managerReducer'
import { PROJECT_HISTORY_UNDO } from '@store/reducers/manager/middleware/history/types'
import { seoForm } from '@store/reducers/seo/reducer'
import { subscriptionReducer } from '@store/reducers/subscription'
import { templatesReducer } from '@store/reducers/templates'
import auth from '@storeOld/reducers/auth'
import canvas from '@storeOld/reducers/canvas/canvas'
import { crawler } from '@storeOld/reducers/crawler'
import { notification } from '@storeOld/reducers/notification'
import { publish } from '@storeOld/reducers/publish'
import ui from '@storeOld/reducers/ui'
import user from '@storeOld/reducers/user'
import { combineReducers, type UnknownAction } from 'redux'

import { accountReducer } from './account'
import { inboxReducer } from './inbox'

const appReducer = combineReducers({
  account: accountReducer,
  router: routerReducer,
  manager: projectManagerReducer,
  relations: relationsCoreReducer,
  library: libraryReducer,
  artboard: artboardReducer,
  canvas,
  auth,
  crawler,
  crawlImport: crawlImportReducer,
  ui,
  user,
  notification,
  publish,
  loaders: loadersReducer,
  confirmation2: confirmationReducer,
  dashboard: dashboardReducer,
  ai: aiReducer,
  ga4,
  seoForm,
  subscription: subscriptionReducer,
  apiKeys: apiKeysReducer,
  inbox: inboxReducer,
  apiUsage: apiUsageReducer,
  templates: templatesReducer,
})

// @ts-expect-error
const reduceProject = (state, action) => {
  switch (action.type) {
    case managerActions.setComments.type: {
      return {
        ...state,
        manager: { ...state.manager, comments: action.payload },
      }
    }
    case managerActions.updateMeta.type: {
      return {
        ...state,
        manager: {
          ...state.manager,
          meta: {
            ...state.manager.meta,
            ...action.payload,
          },
        },
      }
    }
    case managerActions.loadProject.type: {
      return {
        ...state,
        manager: {
          ...initialProjectManagerStore,

          meta: action.payload.meta,
          project: action.payload.project,
        },
        relations: action.payload.relations,
        // The items the symbol refs of the project point at come with the
        // document, so the first render already has them.
        library: libraryReducer(
          undefined,
          LibraryActions.hydrate(action.payload.library ?? {})
        ),
      }
    }
    case managerActions.updateProjectAndRelations.type: {
      return {
        ...state,
        manager: {
          ...state.manager,
          project: action.payload.project,
        },
        relations: action.payload.relations,
      }
    }
    case managerActions.resetProject.type: {
      return {
        ...state,
        manager: initialProjectManagerStore,
        relations: {
          dependencies: {},
        },
        library: initialLibraryStore,
      }
    }
    case 'PROJECT_RESTORE_SNAPSHOT':
    case PROJECT_HISTORY_UNDO: {
      const { project, relations } = action.payload

      return {
        ...state,
        manager: {
          ...state.manager,
          project,
        },
        relations,
      }
    }
    default: {
      return appReducer(state, action)
    }
  }
}

export const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: UnknownAction
) => {
  const nextState = reduceProject(state, action)

  // Clear analytics in the same dispatch that changes the project, before
  // subscribers can render the previous project's connection or metrics.
  if (
    action.type === managerActions.resetProject.type ||
    state?.manager.meta.id !== nextState.manager.meta.id
  ) {
    return { ...nextState, ga4: ga4(undefined, action) }
  }

  return nextState
}
